import React, { useEffect, useRef, useState } from "react";
import { TableCell } from "@mui/material";
import { toast } from "sonner";
import { EDITABLE_PRICE_COLUMNS, saveProductListPrice } from "./saveProductListPrice";

export { EDITABLE_PRICE_COLUMNS };

const ProductPriceInlineCell = ({ row, columnId, value, align = "right", onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const committingRef = useRef(false);
  const skipBlurRef = useRef(false);

  useEffect(() => {
    if (!editing || !inputRef.current) {
      return;
    }
    inputRef.current.focus();
    inputRef.current.select();
  }, [editing]);

  const startEdit = (e) => {
    e.stopPropagation();
    setDraft(value == null || value === "" ? "" : String(value));
    setEditing(true);
  };

  const cancelEdit = () => {
    if (saving || committingRef.current) {
      return;
    }
    skipBlurRef.current = true;
    setEditing(false);
  };

  const commitEdit = async () => {
    if (committingRef.current || saving) {
      return;
    }
    const current = value == null || value === "" ? "" : String(value);
    const next = String(draft).trim();
    if (next === current || (Number(next) === Number(current) && next !== "" && current !== "")) {
      setEditing(false);
      return;
    }
    committingRef.current = true;
    setSaving(true);
    try {
      const updates = await saveProductListPrice({
        productId: row.id,
        columnId,
        value: next,
        row,
      });
      onSaved?.(updates);
      toast.success("Price updated");
      setEditing(false);
    } catch (error) {
      toast.error(error?.message || "Failed to update price");
    } finally {
      setSaving(false);
      committingRef.current = false;
    }
  };

  if (editing) {
    return (
      <TableCell
        data-column-id={columnId}
        align={align}
        onClick={(e) => e.stopPropagation()}
        sx={{ py: 0.25, px: 0.5, minWidth: 70 }}
      >
        <input
          ref={inputRef}
          value={draft}
          disabled={saving}
          inputMode="decimal"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitEdit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              skipBlurRef.current = true;
              cancelEdit();
            }
          }}
          onBlur={() => {
            if (skipBlurRef.current) {
              skipBlurRef.current = false;
              return;
            }
            commitEdit();
          }}
          style={{
            width: "100%",
            minWidth: 70,
            boxSizing: "border-box",
            textAlign: "right",
            fontSize: 10,
            padding: "4px 8px",
            border: "1px solid rgba(145, 158, 171, 0.32)",
            borderRadius: 4,
            outline: "none",
            background: "#fff",
          }}
        />
      </TableCell>
    );
  }

  return (
    <TableCell
      data-column-id={columnId}
      align={align}
      onClick={startEdit}
      sx={{ cursor: "pointer" }}
    >
      {value ?? ""}
    </TableCell>
  );
};

export default ProductPriceInlineCell;
